/*
  Hair by Kelly — motion layer
  Loader, page transitions, smooth scroll, scroll reveals, sticky header that
  switches colour over dark sections. Turn each part on/off in
  Customize → Theme settings → Animations.
*/
(function () {
  'use strict';
  var d = document.documentElement;
  var cfg = window.HBK_CFG || {};
  var gsap = window.gsap;
  var ST = window.ScrollTrigger;
  var hasGsap = !!(gsap && ST);
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var motion = hasGsap && d.classList.contains('hbk-motion');
  var EASE = 'expo.out';
  var lenis = null;

  window.HBKMotion = true;

  function store(fn) { try { return fn(); } catch (e) { return null; } }
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---------- 1. Header: always sticky, colour follows the section under it ---------- */
  function initHeader() {
    var header = $('.hbk-header');
    if (!header) return;
    var darkSel = '.hbk-hero, .bg-ink, .hbk-footer, [data-hbk-dark]';
    var ticking = false;
    function update() {
      ticking = false;
      var r = header.getBoundingClientRect();
      var y = Math.max(1, r.top + r.height / 2);
      var els = document.elementsFromPoint(window.innerWidth / 2, y) || [];
      var dark = false;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el === header || header.contains(el)) continue;
        if (el.closest('.hbk-loader, .hbk-curtain')) continue;
        if (el.closest(darkSel)) { dark = true; }
        break;
      }
      header.classList.toggle('is-dark', dark);
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('shopify:section:load', onScroll);
    update();
    window.HBKHeaderUpdate = update;
  }

  initHeader();

  if (!hasGsap) {
    d.classList.remove('hbk-loading', 'hbk-entering', 'hbk-motion');
    return;
  }
  gsap.registerPlugin(ST);

  /* ---------- 2. Smooth scroll (desktop mouse/trackpad only) ---------- */
  if (motion && cfg.smooth && window.Lenis && finePointer) {
    lenis = new window.Lenis({ lerp: 0.1, wheelMultiplier: 1 });
    lenis.on('scroll', ST.update);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    window.HBKLenis = lenis;
  }
  function lockScroll(on) {
    if (lenis) { on ? lenis.stop() : lenis.start(); }
  }

  /* ---------- helpers ---------- */
  function splitWords(el) {
    if (!el || el.getAttribute('data-split')) return [];
    el.setAttribute('data-split', '1');
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var parts = n.textContent.split(/(\s+)/);
          var frag = document.createDocumentFragment();
          parts.forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
            var o = document.createElement('span'); o.className = 'hbk-w';
            var i = document.createElement('span'); i.className = 'hbk-w__in'; i.textContent = p;
            o.appendChild(i); frag.appendChild(o);
          });
          n.parentNode.replaceChild(frag, n);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') {
          walk(n);
        }
      });
    })(el);
    return $$('.hbk-w__in', el);
  }

  function maskImage(img) {
    var p = img.parentElement;
    if (p && p.classList.contains('hbk-mask')) return p;
    var m = document.createElement('span');
    m.className = 'hbk-mask';
    p.insertBefore(m, img);
    m.appendChild(img);
    return m;
  }

  /* ---------- 3. Loader (first visit in a session) ---------- */
  function runLoader() {
    return new Promise(function (resolve) {
      var loader = $('.hbk-loader');
      if (!loader || !d.classList.contains('hbk-loading')) { resolve(false); return; }
      lockScroll(true);
      var letters = $$('.hbk-loader__logo img', loader);
      var bar = $('.hbk-loader__bar span', loader);
      var count = $('.hbk-loader__count', loader);
      var prog = { v: 0 };
      function paint() {
        if (bar) bar.style.transform = 'scaleX(' + (prog.v / 100) + ')';
        if (count) count.textContent = String(Math.round(prog.v)).padStart(3, '0');
      }
      gsap.from(letters, { yPercent: 105, scale: 0.94, duration: 1.2, ease: EASE });
      gsap.from($$('.hbk-loader__tag, .hbk-loader__meta', loader), { opacity: 0, y: 12, duration: 0.9, ease: EASE, delay: 0.3 });
      var first = gsap.to(prog, { v: 82, duration: 1.4, ease: 'power2.out', onUpdate: paint });

      var loaded = document.readyState === 'complete';
      var done = false;
      function finish() {
        if (done) return; done = true;
        first.kill();
        gsap.timeline({
          onComplete: function () {
            d.classList.remove('hbk-loading');
            store(function () { sessionStorage.setItem('hbk-visited', '1'); });
            lockScroll(false);
            ST.refresh();
          }
        })
          .to(prog, { v: 100, duration: 0.45, ease: 'power2.inOut', onUpdate: paint })
          .to(letters, { yPercent: -105, duration: 0.7, ease: 'expo.in' }, '+=0.1')
          .to($$('.hbk-loader__tag, .hbk-loader__meta, .hbk-loader__bar', loader), { opacity: 0, duration: 0.4 }, '<')
          .to(loader, { yPercent: -100, duration: 1.1, ease: 'expo.inOut' }, '-=0.25')
          .add(function () { resolve(true); }, '-=0.75');
      }
      var minWait = new Promise(function (r) { setTimeout(r, 1500); });
      var pageLoad = new Promise(function (r) {
        if (loaded) r(); else window.addEventListener('load', r, { once: true });
        setTimeout(r, 3800);
      });
      Promise.all([minWait, pageLoad]).then(finish);
    });
  }

  /* ---------- 4. Page transitions ---------- */
  var curtain = $('.hbk-curtain');
  function revealFromCurtain() {
    return new Promise(function (resolve) {
      if (!curtain || !d.classList.contains('hbk-entering')) { resolve(false); return; }
      gsap.set(curtain, { y: 0, yPercent: 0, visibility: 'visible' });
      gsap.timeline({
        onComplete: function () {
          d.classList.remove('hbk-entering');
          gsap.set(curtain, { clearProps: 'all' });
          ST.refresh();
        }
      })
        .to($('.hbk-curtain__logo', curtain), { opacity: 0, y: -16, duration: 0.35, ease: 'power2.in' })
        .to(curtain, { yPercent: -100, duration: 0.9, ease: 'expo.inOut' }, '-=0.05')
        .add(function () { resolve(true); }, '-=0.55');
    });
  }

  function coverAndGo(url) {
    if (!curtain) { window.location.href = url; return; }
    store(function () { sessionStorage.setItem('hbk-nav', '1'); });
    lockScroll(true);
    gsap.timeline({ onComplete: function () { window.location.href = url; } })
      .set(curtain, { visibility: 'visible', y: 0, yPercent: 100 })
      .set($('.hbk-curtain__logo', curtain), { opacity: 0, y: 16 })
      .to(curtain, { yPercent: 0, duration: 0.7, ease: 'expo.inOut' })
      .to($('.hbk-curtain__logo', curtain), { opacity: 1, y: 0, duration: 0.4, ease: EASE }, '-=0.25');
  }

  function initTransitions() {
    if (!cfg.transitions || !motion) return;
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      var raw = a.getAttribute('href') || '';
      if (!raw || raw.charAt(0) === '#' || /^(mailto|tel|sms|javascript):/i.test(raw)) return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      if (/^\/(cart\/(add|change|clear)|account\/logout|checkout)/.test(url.pathname)) return;
      e.preventDefault();
      coverAndGo(url.href);
    });
    // Coming back with the browser Back button: hide the curtain again.
    window.addEventListener('pageshow', function (ev) {
      if (ev.persisted && curtain) {
        gsap.set(curtain, { clearProps: 'all' });
        d.classList.remove('hbk-entering');
        lockScroll(false);
      }
    });
    // Prefetch pages on hover so the next page opens faster.
    var seen = {};
    document.addEventListener('mouseover', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank') return;
      var href = a.href;
      if (seen[href] || href.indexOf(window.location.origin) !== 0 || href.indexOf('#') > -1) return;
      seen[href] = 1;
      var l = document.createElement('link'); l.rel = 'prefetch'; l.href = href;
      document.head.appendChild(l);
    }, { passive: true });
  }

  /* ---------- 5. Hero intro + parallax ---------- */
  var hero = $('.hbk-hero');
  var heroTitleWords = [];
  function prepareHero() {
    if (!hero) return;
    heroTitleWords = splitWords($('.hbk-hero__title', hero));
  }
  function playHero() {
    if (!hero) return;
    var img = $('.hbk-hero__img, img', hero);
    var title = $('.hbk-hero__title', hero);
    var rest = $$('.hbk-hero__inner > *', hero).filter(function (el) { return el !== title; });
    var tl = gsap.timeline({ defaults: { ease: EASE } });
    if (img) tl.fromTo(img, { opacity: 0, scale: 1.18 }, { opacity: 1, scale: 1.04, duration: 2.2 }, 0);
    if (title) {
      tl.set(title, { opacity: 1 }, 0);
      tl.fromTo(heroTitleWords, { yPercent: 115 }, { yPercent: 0, duration: 1.3, stagger: 0.07 }, 0.2);
    }
    tl.fromTo(rest, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 1.1, stagger: 0.1 }, 0.35);
    var extra = $('.hbk-hero__inner + *, .hbk-hero > span', hero);
    if (extra) tl.fromTo(extra, { opacity: 0 }, { opacity: 1, duration: 1 }, 0.9);

    // Parallax while scrolling past the hero
    if (img) gsap.to(img, { yPercent: 10, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
    gsap.to($('.hbk-hero__inner', hero), { yPercent: -18, opacity: 0.15, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
  }

  /* ---------- 6. Scroll reveals ---------- */
  var HEADINGS = '.hbk-h1, .hbk-h2, .hbk-faq-title';
  var FADES = [
    '.hbk-eyebrow', '.hbk-text', '.hbk-rte', '.hbk-btns', '.hbk-btn', '.hbk-link', '.hbk-caption',
    '.hbk-trust span', '.hbk-numlist__item', '.hbk-card', '.hbk-faq details', '.hbk-price-cat li',
    '.hbk-pcard', '.hbk-module li', '.hbk-tags p', '.hbk-footer__col', '.hbk-footer__bar',
    '.hbk-form__row', '.hbk-field', '.hbk-cart-line', '.hbk-product__thumbs'
  ].join(',');
  var IMAGES = '.hbk-split__img, .hbk-gallery img, .hbk-product__media > img';
  var SKIP = '.hbk-hero, .hbk-header, .hbk-announce, .hbk-loader, .hbk-curtain, .hbk-mnav__panel';

  function usable(el) { return !el.closest(SKIP) && !el.getAttribute('data-revealed'); }

  function prepareReveals(root) {
    var heads = $$(HEADINGS, root).filter(usable);
    heads.forEach(function (h) {
      var words = splitWords(h);
      h.setAttribute('data-revealed', '1');
      if (!words.length) return;
      gsap.set(words, { yPercent: 115 });
      ST.create({
        trigger: h, start: 'top 88%', once: true,
        onEnter: function () { whenReady(function () { gsap.to(words, { yPercent: 0, duration: 1.2, ease: EASE, stagger: 0.05 }); }); }
      });
    });

    var fades = $$(FADES, root).filter(usable).filter(function (el) {
      var p = el.parentElement && el.parentElement.closest(FADES);
      return !p || p.closest(SKIP);
    });
    fades.forEach(function (el) { el.setAttribute('data-revealed', '1'); });
    if (fades.length) {
      gsap.set(fades, { opacity: 0, y: 30 });
      ST.batch(fades, {
        start: 'top 92%', once: true,
        onEnter: function (batch) {
          whenReady(function () { gsap.to(batch, { opacity: 1, y: 0, duration: 1.1, ease: EASE, stagger: 0.08, overwrite: true, clearProps: 'transform,opacity' }); });
        }
      });
    }

    $$(IMAGES, root).filter(usable).forEach(function (img) {
      img.setAttribute('data-revealed', '1');
      var m = maskImage(img);
      gsap.set(m, { clipPath: 'inset(100% 0% 0% 0%)' });
      gsap.set(img, { scale: 1.25 });
      ST.create({
        trigger: m, start: 'top 85%', once: true,
        onEnter: function () { whenReady(function () {
          gsap.to(m, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'expo.inOut', clearProps: 'clipPath' });
          gsap.to(img, { scale: 1.08, duration: 1.8, ease: EASE });
        }); }
      });
      // gentle parallax inside the mask
      gsap.fromTo(img, { yPercent: -4 }, { yPercent: 4, ease: 'none', scrollTrigger: { trigger: m, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    $$('.hbk-pcard__img', root).filter(usable).forEach(function (box) {
      box.setAttribute('data-revealed', '1');
      gsap.set(box, { clipPath: 'inset(0% 0% 100% 0%)' });
      ST.create({ trigger: box, start: 'top 90%', once: true, onEnter: function () { whenReady(function () {
        gsap.to(box, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.2, ease: 'expo.inOut', clearProps: 'clipPath' });
      }); } });
    });

    // Thin lines that draw in
    $$('.hbk-numlist__items, .hbk-trust', root).filter(usable).forEach(function (el) {
      el.setAttribute('data-revealed', '1');
      el.classList.add('hbk-line-ready');
      ST.create({ trigger: el, start: 'top 90%', once: true, onEnter: function () { whenReady(function () { el.classList.add('hbk-line-in'); }); } });
    });
  }

  /* ---------- 7. Small touches ---------- */
  function initFaq() {
    if (!motion) return;
    $$('.hbk-faq details').forEach(function (dt) {
      var sum = $('summary', dt);
      var body = dt.querySelector('summary + *');
      if (!sum || !body || dt.getAttribute('data-faq')) return;
      dt.setAttribute('data-faq', '1');
      sum.addEventListener('click', function (e) {
        e.preventDefault();
        if (dt.open) {
          gsap.to(body, { height: 0, paddingBottom: 0, opacity: 0, duration: 0.5, ease: 'expo.inOut', onComplete: function () {
            dt.open = false; gsap.set(body, { clearProps: 'height,paddingBottom,opacity' }); ST.refresh();
          } });
        } else {
          dt.open = true;
          gsap.fromTo(body, { height: 0, paddingBottom: 0, opacity: 0 }, { height: 'auto', paddingBottom: '1.25rem', opacity: 1, duration: 0.7, ease: EASE, onComplete: function () {
            gsap.set(body, { clearProps: 'height,paddingBottom' }); ST.refresh();
          } });
        }
      });
    });
  }

  function initAnchors() {
    if (!lenis) return;
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var t = document.querySelector(id);
      if (!t) return;
      e.preventDefault();
      lenis.scrollTo(t, { offset: -90 });
    });
  }

  /* ---------- boot ---------- */
  var intro = d.classList.contains('hbk-loading') ? runLoader() : revealFromCurtain();
  function whenReady(fn) { intro.then(fn); }
  if (motion) { prepareHero(); prepareReveals(document); }
  initTransitions();
  initFaq();
  initAnchors();

  intro.then(function () {
    if (motion) playHero();
    if (window.HBKHeaderUpdate) window.HBKHeaderUpdate();
  });

  window.addEventListener('load', function () { ST.refresh(); });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ST.refresh(); });
})();
